import clsx from "clsx";
import { FC, ReactNode, useState } from "react";

import { ContentText, RequireMedia } from "@tsmono/react/components";
import { isRenderableImageSource } from "@tsmono/util";

import { MessageContent } from "../MessageContent";

import styles from "./AnnotatedScreenshot.module.css";
import {
  CLICK_ACTIONS,
  type ScreenshotContent,
  type ToolAnnotation,
} from "./browserActionUtils";

interface AnnotatedScreenshotOutputProps {
  contents: ScreenshotContent[];
  annotation: ToolAnnotation;
}

/**
 * Renders a tool result's content with the action annotation drawn over its
 * screenshot. Only the last renderable image is annotated: results may carry
 * text alongside the screenshot, and the last image reflects the page state
 * the action targeted.
 */
export const AnnotatedScreenshotOutput: FC<AnnotatedScreenshotOutputProps> = ({
  contents,
  annotation,
}) => {
  const annotatedIndex = contents.findLastIndex(
    (c) => c.type === "image" && isRenderableImageSource(c.image)
  );

  return (
    <>
      {contents.map((c, i) =>
        i === annotatedIndex && c.type === "image" ? (
          <AnnotatedScreenshot key={i} src={c.image} annotation={annotation} />
        ) : (
          <MessageContent key={i} contents={[c]} />
        )
      )}
    </>
  );
};

interface AnnotatedScreenshotProps {
  src: string;
  annotation: ToolAnnotation;
}

/**
 * A screenshot with the action annotation drawn on top. The SVG overlay's
 * viewBox is the image's natural size, so annotation coordinates (which are
 * in screenshot pixels) map to display position without any JS measurement,
 * and the overlay rescales with the image automatically.
 */
export const AnnotatedScreenshot: FC<AnnotatedScreenshotProps> = ({
  src,
  annotation,
}) => {
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  }>();

  return (
    <RequireMedia kind="screenshot">
      <div className={styles.container}>
        <img
          src={src}
          alt="Annotated screenshot"
          className={styles.screenshot}
          onLoad={(e) =>
            setNaturalSize({
              width: e.currentTarget.naturalWidth,
              height: e.currentTarget.naturalHeight,
            })
          }
        />
        {naturalSize && naturalSize.width > 0 && naturalSize.height > 0 && (
          <svg
            viewBox={`0 0 ${naturalSize.width} ${naturalSize.height}`}
            preserveAspectRatio="none"
            className={styles.overlay}
          >
            {renderSvgAnnotation(annotation)}
          </svg>
        )}
        {renderHtmlAnnotation(annotation)}
      </div>
    </RequireMedia>
  );
};

export function renderSvgAnnotation(annotation: ToolAnnotation): ReactNode {
  const { action, coordinate } = annotation;

  if (CLICK_ACTIONS.has(action) && coordinate) {
    const [x, y] = coordinate;

    return (
      <g transform={`translate(${x}, ${y})`}>
        <circle
          cx="0"
          cy="0"
          r="16"
          stroke="rgba(239,68,68,0.8)"
          strokeWidth="3"
          fill="none"
          filter="drop-shadow(0 0 6px rgba(239,68,68,0.4))"
        />
        <svg viewBox="0 0 32 32" width="30" height="30" x="-10" y="-7">
          <g fill="none" fillRule="evenodd" transform="translate(10 7)">
            <path
              d="m6.148 18.473 1.863-1.003 1.615-.839-2.568-4.816h4.332l-11.379-11.408v16.015l3.316-3.221z"
              fill="#fff"
            />
            <path
              d="m6.431 17 1.765-.941-2.775-5.202h3.604l-8.025-8.043v11.188l2.53-2.442z"
              fill="#000"
            />
          </g>
        </svg>
      </g>
    );
  }

  if (action === "scroll" && coordinate) {
    const [x, y] = coordinate;

    let arrow = "↕";
    if (annotation.scrollDirection) {
      const dir = annotation.scrollDirection.toLowerCase();
      if (dir.includes("up")) arrow = "↑";
      else if (dir.includes("down")) arrow = "↓";
      else if (dir.includes("left")) arrow = "←";
      else if (dir.includes("right")) arrow = "→";
    }

    return (
      <g transform={`translate(${x}, ${y})`}>
        <circle cx="0" cy="0" r="18" fill="rgba(59,130,246,0.8)" />
        <text
          x="0"
          y="0"
          fill="white"
          fontSize="20"
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight="bold"
        >
          {arrow}
        </text>
      </g>
    );
  }

  return null;
}

export function renderHtmlAnnotation(annotation: ToolAnnotation): ReactNode {
  const { action, text } = annotation;

  if (action === "type" || action === "key") {
    const isKey = action === "key";

    return (
      <div className={clsx(styles.badge, isKey ? styles.key : styles.type)}>
        {"⌨ "}
        <ContentText text={text || ""} />
      </div>
    );
  }

  return null;
}
